// Web Audio API playback engine: decode, trim, fade, loop, and expose
// waveform peak data for the trimmer UI. Kept independent from React state
// churn as much as possible — elapsed time for the canvas loop is read
// directly from the AudioContext clock, never from component state.
import { useCallback, useEffect, useRef, useState } from 'react';
import { uploadVideoForAudioExtraction, fetchUploadedAudio } from '../lib/renderApi';

const VIDEO_FILE_RE = /\.(mp4|mov|m4v|webm|mkv)$/i;

function computePeaks(audioBuffer, buckets = 700) {
  const channel = audioBuffer.getChannelData(0);
  const samplesPerBucket = Math.max(1, Math.floor(channel.length / buckets));
  const peaks = [];
  for (let i = 0; i < buckets; i++) {
    const start = i * samplesPerBucket;
    let min = 1, max = -1;
    for (let j = 0; j < samplesPerBucket; j++) {
      const v = channel[start + j];
      if (v === undefined) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    peaks.push({ min: min === 1 ? 0 : min, max: max === -1 ? 0 : max });
  }
  return peaks;
}

export function useAudioEngine() {
  const [state, setState] = useState({
    fileName: null,
    duration: 0,
    trimStart: 0,
    trimEnd: 0,
    fadeIn: 0,
    fadeOut: 0,
    isPlaying: false,
    currentTime: 0,
    peaks: null,
    error: null,
    isLoading: false,
    isExtracting: false,
    extractProgress: 0,
    noAudio: false,
    noAudioDuration: 15,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const ctxRef = useRef(null);
  const bufferRef = useRef(null);
  const sourceRef = useRef(null);
  const gainRef = useRef(null);
  const startedAtRef = useRef(0);
  const pausedOffsetRef = useRef(0);
  const rafRef = useRef(null);
  const objectUrlRef = useRef(null);
  const fileRef = useRef(null);

  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === 'suspended') ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const stopInternal = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.onended = null; sourceRef.current.stop(); } catch (e) {}
      try { sourceRef.current.disconnect(); } catch (e) {}
      sourceRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const loadFile = useCallback(async (file) => {
    setState((s) => ({ ...s, isLoading: true, isExtracting: false, extractProgress: 0, error: null }));
    let extractedFromVideo = false;
    try {
      const looksLikeVideo = file.type.startsWith('video/') || VIDEO_FILE_RE.test(file.name);
      if (looksLikeVideo) {
        extractedFromVideo = true;
        setState((s) => ({ ...s, isExtracting: true }));
        try {
          const { file_id } = await uploadVideoForAudioExtraction(file, (f) => {
            setState((s) => ({ ...s, extractProgress: f }));
          });
          const blob = await fetchUploadedAudio(file_id);
          const baseName = file.name.replace(/\.[^./\\]+$/, '') || 'extracted-audio';
          file = new File([blob], `${baseName}.m4a`, { type: 'audio/mp4' });
        } catch (extractErr) {
          throw new Error(extractErr.message || 'Could not extract audio from that video.');
        } finally {
          setState((s) => ({ ...s, isExtracting: false }));
        }
      }
      const looksLikeAudio = file.type.startsWith('audio/') || /\.(mp3|wav|m4a|flac|aac|ogg)$/i.test(file.name);
      if (!looksLikeAudio && file.size === 0) {
        throw new Error('Unsupported audio format. Use mp3, wav, m4a, or flac.');
      }
      const arrayBuffer = await file.arrayBuffer();
      const ctx = getCtx();
      let audioBuffer;
      try {
        audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
      } catch (decodeErr) {
        throw new Error(extractedFromVideo
          ? 'We pulled the audio out of your video but your browser could not play it back. Please try a different video.'
          : 'Could not decode that audio file. Please use mp3, wav, m4a, or flac.');
      }
      stopInternal();
      bufferRef.current = audioBuffer;
      fileRef.current = file;
      pausedOffsetRef.current = 0;
      const peaks = computePeaks(audioBuffer);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = URL.createObjectURL(file);
      setState((s) => ({
        ...s,
        fileName: file.name,
        duration: audioBuffer.duration,
        trimStart: 0,
        trimEnd: Math.min(30, audioBuffer.duration),
        fadeIn: 0,
        fadeOut: 0,
        isPlaying: false,
        currentTime: 0,
        peaks,
        isLoading: false,
        isExtracting: false,
        error: null,
      }));
    } catch (e) {
      setState((s) => ({ ...s, isLoading: false, isExtracting: false, error: e.message || 'Could not load that audio file.' }));
    }
  }, [getCtx, stopInternal]);

  const tick = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || !sourceRef.current) return;
    const elapsed = pausedOffsetRef.current + (ctx.currentTime - startedAtRef.current);
    setState((s) => ({ ...s, currentTime: Math.min(elapsed, Math.max(0, s.trimEnd - s.trimStart)) }));
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const play = useCallback(() => {
    if (!bufferRef.current) return;
    const ctx = getCtx();
    // stopInternal + node creation must happen synchronously, back-to-back,
    // outside of setState. setState updaters run on React's own schedule,
    // so if play() were re-entered before a queued updater actually ran,
    // sourceRef.current would still look empty and stopInternal() would
    // miss the in-flight node -- that's what caused overlapping playback.
    stopInternal();
    const s = stateRef.current;
    const windowDur = Math.max(0.01, s.trimEnd - s.trimStart);
    const offset = pausedOffsetRef.current >= windowDur ? 0 : pausedOffsetRef.current;
    const source = ctx.createBufferSource();
    source.buffer = bufferRef.current;
    const gain = ctx.createGain();
    source.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    const remaining = windowDur - offset;
    const fadeIn = Math.min(s.fadeIn, windowDur / 2);
    const fadeOut = Math.min(s.fadeOut, windowDur / 2);
    if (fadeIn > 0 && offset < fadeIn) {
      gain.gain.setValueAtTime(offset / fadeIn, now);
      gain.gain.linearRampToValueAtTime(1, now + (fadeIn - offset));
    } else {
      gain.gain.setValueAtTime(1, now);
    }
    const fadeOutStart = windowDur - fadeOut;
    if (fadeOut > 0 && offset < windowDur) {
      const delay = Math.max(0, fadeOutStart - offset);
      gain.gain.setValueAtTime(1, now + delay);
      gain.gain.linearRampToValueAtTime(0.0001, now + delay + Math.min(fadeOut, remaining));
    }
    source.start(0, s.trimStart + offset, remaining);
    startedAtRef.current = now;
    pausedOffsetRef.current = offset;
    sourceRef.current = source;
    gainRef.current = gain;
    source.onended = () => {
      if (sourceRef.current === source) {
        pausedOffsetRef.current = 0;
        sourceRef.current = null;
        play();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    setState((prev) => ({ ...prev, isPlaying: true }));
  }, [getCtx, stopInternal, tick]);

  const pause = useCallback(() => {
    setState((s) => {
      if (ctxRef.current && sourceRef.current) {
        pausedOffsetRef.current = pausedOffsetRef.current + (ctxRef.current.currentTime - startedAtRef.current);
      }
      stopInternal();
      return { ...s, isPlaying: false };
    });
  }, [stopInternal]);

  const seek = useCallback((offsetSeconds) => {
    pausedOffsetRef.current = Math.max(0, offsetSeconds);
    setState((s) => ({ ...s, currentTime: pausedOffsetRef.current }));
    if (state.isPlaying) {
      stopInternal();
      play();
    }
  }, [state.isPlaying, play, stopInternal]);

  const restart = useCallback(() => {
    seek(0);
  }, [seek]);

  const skip = useCallback((deltaSeconds) => {
    const windowDur = Math.max(0.01, state.trimEnd - state.trimStart);
    const current = pausedOffsetRef.current + (state.isPlaying && ctxRef.current ? (ctxRef.current.currentTime - startedAtRef.current) : 0);
    seek(Math.max(0, Math.min(windowDur, current + deltaSeconds)));
  }, [seek, state.isPlaying, state.trimEnd, state.trimStart]);

  const setTrim = useCallback((trimStart, trimEnd) => {
    setState((s) => {
      const ts = Math.max(0, Math.min(trimStart, s.duration));
      const te = Math.max(ts + 0.1, Math.min(trimEnd, s.duration));
      return { ...s, trimStart: ts, trimEnd: te };
    });
    pausedOffsetRef.current = 0;
    if (state.isPlaying) { stopInternal(); play(); }
  }, [play, stopInternal, state.isPlaying]);

  const setFadeIn = useCallback((v) => setState((s) => ({ ...s, fadeIn: Math.max(0, Math.min(3, v)) })), []);
  const setFadeOut = useCallback((v) => setState((s) => ({ ...s, fadeOut: Math.max(0, Math.min(3, v)) })), []);

  const setNoAudio = useCallback((v) => setState((s) => ({ ...s, noAudio: !!v })), []);
  const setNoAudioDuration = useCallback(
    (v) => setState((s) => ({ ...s, noAudioDuration: Math.max(1, Math.min(300, v)) })),
    [],
  );

  const getElapsed = useCallback(() => {
    if (!ctxRef.current || !sourceRef.current) return pausedOffsetRef.current;
    return pausedOffsetRef.current + (ctxRef.current.currentTime - startedAtRef.current);
  }, []);

  const reset = useCallback(() => {
    stopInternal();
    bufferRef.current = null;
    fileRef.current = null;
    pausedOffsetRef.current = 0;
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
    setState({
      fileName: null, duration: 0, trimStart: 0, trimEnd: 0, fadeIn: 0, fadeOut: 0,
      isPlaying: false, currentTime: 0, peaks: null, error: null, isLoading: false,
      isExtracting: false, extractProgress: 0, noAudio: false, noAudioDuration: 15,
    });
  }, [stopInternal]);

  useEffect(() => () => stopInternal(), [stopInternal]);

  return {
    ...state,
    loadFile,
    play,
    pause,
    restart,
    skip,
    seek,
    setTrim,
    setFadeIn,
    setFadeOut,
    setNoAudio,
    setNoAudioDuration,
    getElapsed,
    reset,
    hasAudio: !!bufferRef.current,
    audioBuffer: bufferRef.current,
    file: fileRef.current,
  };
}
